import React, { useEffect, useState } from 'react';
import PageApp from '../../../Page';
import { HomeOutlined, UnorderedListOutlined, PlusCircleOutlined } from '@ant-design/icons';
import { Form, Divider, Button, DatePicker, Select, Row, Col, Card, Space, InputNumber } from 'antd';
import RangoEmpleadosInput, { RangoEmpleadosLabel } from '../../../../utils/RangoEmpleadosInput';
import AnioPreNomina from '../../../../components/forms/anio_prenomina';
import { getList as getHorarios } from '../../../../apis/OdooScheduleApi';
import { getList as getDepartamentos } from '../../../../apis/OdooDepartamentoApi';
import { getList as getPuestos } from '../../../../apis/OdooPuestoApi';
import { getList as getAreas } from '../../../../apis/AreasTrabajoApi';
import { getAll as getSupervisores } from '../../../../apis/SupervisoresApi';
import { getAll as getMotivos } from '../../../../apis/MotivosApi';
import { createMassive } from '../../../../apis/DefinicionAutorizacionApi';
import TIPO_AUTORIZACION_OPTIONS from '../../../../constants/tipoAutorizacion';
import { message } from 'antd';
import dayjs from 'dayjs';

const { Option } = Select;
const { RangePicker } = DatePicker;

const routes = [
    {
        href: '',
        title: <>
            <HomeOutlined />
            <span>Inicio</span>
        </>,
    },
    {
        href: '',
        title: (
            <>
                <UnorderedListOutlined />
                <span>Asistencia</span>
            </>
        ),
    },
    {
        href: '',
        title: (
            <>
                <UnorderedListOutlined />
                <span>Procesos</span>
            </>
        ),
    },
    {
        href: '',
        title: (
            <>
                <PlusCircleOutlined />
                <span>Autorizaciones Globales</span>
            </>
        ),
    }
];

const AutorizacionesGlobalesPage = () => {
    const [form] = Form.useForm();
    const [formFilters] = Form.useForm();
    const [period, setPeriod] = useState(null);
    const [anio, setAnio] = useState(null);
    const [fechaInicial, setFechaInicial] = useState(null);
    const [fechaFinal, setFechaFinal] = useState(null);
    const [disabledForm, setDisabled] = useState(true);

    // Estados para los catálogos
    const [horarios, setHorarios] = useState([]);
    const [puestos, setPuestos] = useState([]);
    const [departamentos, setDepartamentos] = useState([]);
    const [areas, setAreas] = useState([]);
    const [supervisores, setSupervisores] = useState([]);
    const [motivos, setMotivos] = useState([]);

    useEffect(() => {
        (async () => {
            await getCatalogs();
        })();
    }, []);

    const getCatalogs = async () => {
        const responseHorarios = await getHorarios();
        if (responseHorarios.ok) {
            setHorarios(responseHorarios.payload);
        }

        const responseDepartamentos = await getDepartamentos();
        if (responseDepartamentos.ok) {
            setDepartamentos(responseDepartamentos.payload);
        }

        const responseAreas = await getAreas();
        if (responseAreas.ok) {
            setAreas(responseAreas.payload);
        }

        const responseSupervisores = await getSupervisores();
        if (responseSupervisores.ok) {
            setSupervisores(responseSupervisores.payload);
        }

        const responsePuestos = await getPuestos();
        if (responsePuestos.ok) {
            setPuestos(responsePuestos.payload);
        }

        const responseMotivos = await getMotivos();
        if (responseMotivos.ok) {
            let items = responseMotivos.payload.map(item => ({
                value: item.id,
                text: item.clave,
                id: item.id
            }));
            setMotivos(items);
        }
    };

    const onBlurHeader = async (values, periodData) => {
        if (values?.anio && periodData) {
            setAnio(values.anio);
            setPeriod(periodData);
            setDisabled(false);

            // Establecer las fechas del periodo en el RangePicker
            if (periodData.periodoCompleto?.fecha_inicial && periodData.periodoCompleto?.fecha_final) {
                const fechaIni = dayjs(periodData.periodoCompleto.fecha_inicial);
                const fechaFin = dayjs(periodData.periodoCompleto.fecha_final);
                setFechaInicial(fechaIni);
                setFechaFinal(fechaFin);
                form.setFieldsValue({
                    rango_fechas: [fechaIni, fechaFin]
                });
            }
        } else {
            setAnio(null);
            setPeriod(null);
            setDisabled(true);
            setFechaInicial(null);
            setFechaFinal(null);
            form.resetFields();
        }
    };

    const onChangeRangoFechas = (dates) => {
        if (dates && dates.length === 2) {
            setFechaInicial(dates[0]);
            setFechaFinal(dates[1]);
        } else {
            setFechaInicial(null);
            setFechaFinal(null);
        }
    };

    const onGenerarAutorizaciones = async () => {
        try {
            const values = await form.validateFields();
            const filters = await formFilters.getFieldsValue();

            // Validar fechas
            if (!fechaInicial || !fechaFinal) {
                message.warning('Debe seleccionar un rango de fechas válido');
                return;
            }

            // Validar horas
            if (!values.horas || values.horas === 0) {
                message.warning('Las horas deben ser diferentes de 0');
                return;
            }

            // Validar rango de empleados
            if (filters.empleado_rango && filters.empleado_rango.trim()) {
                const rangoPattern = /^\s*(\d+\s*-\s*\d+|\d+)(\s*,\s*(\d+\s*-\s*\d+|\d+))*\s*$/;
                if (!rangoPattern.test(filters.empleado_rango.trim())) {
                    message.warning('El formato de rango de empleados no es válido. Use el formato: 1-7, 50, 150, 200-300');
                    return;
                }
            }

            // Construir el payload
            const payload = {
                fecha_inicial: fechaInicial.format('YYYY-MM-DD'),
                fecha_final: fechaFinal.format('YYYY-MM-DD'),
                tipo_autorizacion: values.tipo_autorizacion,
                horas: values.horas,
                motivo_id: values.motivo_id,
                comentarios: values.comentarios || null,
            };

            if (filters.horario_id) {
                payload.horario_id = filters.horario_id;
            }
            if (filters.puesto_id) {
                payload.job_id = filters.puesto_id;
            }
            if (filters.departamento_id) {
                payload.departamento_id = filters.departamento_id;
            }
            if (filters.area_id) {
                payload.area_id = filters.area_id;
            }
            if (filters.supervisor_id) {
                payload.supervisor_id = filters.supervisor_id;
            }

            if (filters.empleado_rango && filters.empleado_rango.trim()) {
                payload.empleado_rango = filters.empleado_rango.trim();
            }

            const hide = message.loading('Generando autorizaciones...', 0);

            try {
                const response = await createMassive(payload);

                hide();

                if (response.ok) {
                    const result = response.payload;
                    message.success(
                        `${result.message}. Empleados procesados: ${result.empleados_procesados}, Autorizaciones generadas: ${result.autorizaciones_generadas}`,
                        5
                    );

                    form.resetFields(['tipo_autorizacion', 'horas', 'motivo_id', 'comentarios']);
                } else {
                    message.error('Error al generar las autorizaciones');
                }
            } catch (error) {
                hide();
                console.error('Error al generar autorizaciones:', error);
                message.error(error.message || 'Error al generar las autorizaciones');
            }

        } catch (error) {
            console.error('Error al validar:', error);
            message.error('Por favor complete todos los campos requeridos');
        }
    };

    return (
        <PageApp
            routes={routes}
            title={'Autorizaciones Globales'}
        >
            <>
                <AnioPreNomina
                    onBlur={onBlurHeader}
                    form={form}
                    disabledForm={false}
                />
                <Divider />

                <Form
                    layout="vertical"
                    form={form}
                    disabled={disabledForm}
                >
                    <Card title="Configuración de Autorización" style={{ marginBottom: 16 }}>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    name="rango_fechas"
                                    label="Rango de Fechas"
                                    rules={[
                                        {
                                            required: true,
                                            message: 'Por favor selecciona el rango de fechas',
                                        },
                                    ]}
                                >
                                    <RangePicker
                                        style={{ width: '100%' }}
                                        format="DD/MM/YYYY"
                                        onChange={onChangeRangoFechas}
                                        placeholder={['Fecha Inicial', 'Fecha Final']}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col span={8}>
                                <Form.Item
                                    name="tipo_autorizacion"
                                    label="Tipo de Autorización"
                                    rules={[
                                        {
                                            required: true,
                                            message: 'Por favor selecciona el tipo de autorización',
                                        },
                                    ]}
                                >
                                    <Select
                                        placeholder="Selecciona el tipo de autorización"
                                        showSearch
                                        optionFilterProp="children"
                                    >
                                        {TIPO_AUTORIZACION_OPTIONS.map((item) => (
                                            <Option key={item.value} value={item.value}>
                                                {item.text}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>

                            <Col span={8}>
                                <Form.Item
                                    name="horas"
                                    label="Horas"
                                    rules={[
                                        {
                                            required: true,
                                            message: 'Por favor ingresa las horas',
                                        },
                                        {
                                            validator: (_, value) => {
                                                if (value === 0 || value === null || value === undefined) {
                                                    return Promise.reject('Las horas deben ser diferentes de 0');
                                                }
                                                return Promise.resolve();
                                            }
                                        }
                                    ]}
                                >
                                    <InputNumber
                                        style={{ width: '100%' }}
                                        placeholder="Ej: 8"
                                        min={0.01}
                                        step={0.5}
                                    />
                                </Form.Item>
                            </Col>

                            <Col span={8}>
                                <Form.Item
                                    name="motivo_id"
                                    label="Motivo"
                                >
                                    <Select
                                        placeholder="Selecciona un motivo"
                                        allowClear
                                        showSearch
                                        optionFilterProp="children"
                                    >
                                        {motivos.map((item) => (
                                            <Option key={item.id} value={item.value}>
                                                {item.text}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>
                    </Card>

                    <Card title="Filtros" style={{ marginBottom: 16 }}>
                        <Form form={formFilters} layout="vertical">
                            <Row gutter={16}>
                                <Col span={8}>
                                    <Form.Item
                                        name="horario_id"
                                        label="Horario"
                                    >
                                        <Select
                                            placeholder="Selecciona un horario"
                                            allowClear
                                            showSearch
                                            optionFilterProp="children"
                                        >
                                            {horarios.map((item) => (
                                                <Option key={item.id} value={item.id}>
                                                    {item.codigo_corto}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>

                                <Col span={8}>
                                    <Form.Item
                                        name="puesto_id"
                                        label="Puesto"
                                    >
                                        <Select
                                            placeholder="Selecciona un puesto"
                                            allowClear
                                            showSearch
                                            optionFilterProp="children"
                                        >
                                            {puestos.map((item) => (
                                                <Option key={item.value} value={item.value}>
                                                    {item.text}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>

                                <Col span={8}>
                                    <Form.Item
                                        name="departamento_id"
                                        label="Departamento"
                                    >
                                        <Select
                                            placeholder="Selecciona un departamento"
                                            allowClear
                                            showSearch
                                            optionFilterProp="children"
                                        >
                                            {departamentos.map((item) => (
                                                <Option key={item.value} value={item.value}>
                                                    {item.text}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Row gutter={16}>
                                <Col span={8}>
                                    <Form.Item
                                        name="area_id"
                                        label="Área"
                                    >
                                        <Select
                                            placeholder="Selecciona un área"
                                            allowClear
                                            showSearch
                                            optionFilterProp="children"
                                        >
                                            {areas.map((item) => (
                                                <Option key={item.value} value={item.value}>
                                                    {item.text}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>

                                <Col span={8}>
                                    <Form.Item
                                        name="supervisor_id"
                                        label="Supervisor"
                                    >
                                        <Select
                                            placeholder="Selecciona un supervisor"
                                            allowClear
                                            showSearch
                                            optionFilterProp="children"
                                        >
                                            {supervisores.map((item) => (
                                                <Option key={item.id} value={item.id}>
                                                    {item.nombre}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Form.Item
                                name="empleado_rango"
                                label={<RangoEmpleadosLabel />}
                            >
                                <RangoEmpleadosInput />
                            </Form.Item>
                        </Form>
                    </Card>

                    <Row>
                        <Col span={24} style={{ textAlign: 'right' }}>
                            <Space>
                                <Button onClick={() => {
                                    form.resetFields();
                                    formFilters.resetFields();
                                }}>
                                    Limpiar
                                </Button>
                                <Button
                                    type="primary"
                                    onClick={onGenerarAutorizaciones}
                                    disabled={disabledForm}
                                >
                                    Generar Autorizaciones
                                </Button>
                            </Space>
                        </Col>
                    </Row>
                </Form>
            </>
        </PageApp>
    );
};

export default AutorizacionesGlobalesPage;